import React, { useMemo, useState, useEffect } from "react";
import { Search, ChevronDown, X } from "lucide-react";
import { db } from "../firebase";
import { collection, onSnapshot, query, orderBy } from "firebase/firestore";
import ilceKoyler from "../json/ilce-koyler.json";
import CelebDetailModal from "../components/CelebDetailModal";

/* ─── Avatar ─── */
const Avatar = ({ name, imageUrl }) => {
  const [imgError, setImgError] = useState(false);

  const initials = (name || "")
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  if (imageUrl && !imgError) {
    return (
      <div className="w-16 h-16 rounded-xl overflow-hidden shrink-0 border border-slate-200 bg-slate-100">
        <img
          src={imageUrl}
          alt={name}
          loading="lazy"
          onError={() => setImgError(true)}
          className="w-full h-full object-cover"
        />
      </div>
    );
  }

  return (
    <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-amber-200 to-amber-400 text-gray-900 font-bold flex items-center justify-center shadow-sm shrink-0">
      {initials || "Ü"}
    </div>
  );
};

const SourceBadge = ({ source }) => {
  if (source !== "fb") return null;
  return (
    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium shrink-0">
      Yeni
    </span>
  );
};

/* ─── Firestore mapper ─── */
function mapFirestoreCeleb(doc) {
  const d = doc.data() || {};
  return {
    id: doc.id,
    source: "fb",
    name: d.name || "",
    profession: d.profession || "",
    born: d.birthYear ? parseInt(d.birthYear, 10) || null : null,
    hometown: d.sivasIlce || "",
    koy: d.sivasKoy || "",
    bio: d.bio || "",
    gender: d.gender || "",
    imageUrl: d.imageUrl || "",
    isActive: d.isActive || false,
    isPassive: d.isPassive || false,
    isDeceased: d.isDeceased || false,
    tags: d.tags || [],
    createdAt: d.createdAt || null,
  };
}

/* ─── FilterSelect ─── */
const FilterSelect = ({ value, onChange, children }) => (
  <div className="relative">
    <select
      value={value}
      onChange={onChange}
      className="w-full appearance-none pl-3 pr-8 py-2 text-sm bg-white border border-slate-200 rounded-lg text-slate-700
        focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent transition"
    >
      {children}
    </select>
    <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
  </div>
);

/* ─── Ana Bileşen ─── */
export default function CelebsSivasPage({ setCurrentPage }) {
  const [fbCelebs, setFbCelebs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [profession, setProfession] = useState("");
  const [hometown, setHometown] = useState("");
  const [sortBy, setSortBy] = useState("name-asc");
  const [fbPoliticians, setFbPoliticians] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [partyFilter, setPartyFilter] = useState("");
  const [genderFilter, setGenderFilter] = useState("");
  const [koyFilter, setKoyFilter] = useState("");
  const [selectedPerson, setSelectedPerson] = useState(null);

  useEffect(() => {
    try {
      const q = query(collection(db, "unluler"), orderBy("createdAt", "desc"));
      const unsub = onSnapshot(
        q,
        (snap) => {
          setFbCelebs(snap.docs.map(mapFirestoreCeleb));
          setLoading(false);
        },
        (err) => {
          console.error(err);
          setError("Veriler yüklenirken bir hata oluştu.");
          setLoading(false);
        }
      );
      return () => unsub();
    } catch (e) {
      setError("Veriler yüklenirken bir hata oluştu.");
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const q = query(collection(db, "siyasiler"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      setFbPoliticians(
        snap.docs.map((doc) => {
          const d = doc.data() || {};
          return {
            id: `pol_${doc.id}`,
            source: "fb",
            type: "politician",
            name: d.name || "",
            profession: d.position || "", // görev → profession alanına map
            born: d.birthYear ? parseInt(d.birthYear, 10) || null : null,
            hometown: d.sivasIlce || "",
            koy: d.sivasKoy || "",
            bio: d.bio || "",
            gender: d.gender || "",
            imageUrl: d.imageUrl || "",
            tags: d.tags || [],
            party: d.party || "",
            isActive: d.isActive || false,
            isPassive: d.isPassive || false,
            isDeceased: d.isDeceased || false,
            createdAt: d.createdAt || null,
          };
        })
      );
    });
    return () => unsub();
  }, []);

  const parties = useMemo(() => {
    const map = new Map();
    fbPoliticians.forEach((p) => {
      if (!p.party) return;
      const normalized = p.party.trim().toLocaleUpperCase("tr");
      if (!map.has(normalized)) map.set(normalized, p.party.trim());
    });
    return [...map.keys()].sort((a, b) => a.localeCompare(b, "tr"));
  }, [fbPoliticians]);

  /* Birleşik liste — FB önce (yeni eklenenler üstte) */
  const merged = useMemo(
    () => [...fbCelebs, ...fbPoliticians],
    [fbCelebs, fbPoliticians]
  );

  /* Dropdown seçenekleri — birleşik listeden */
  const professions = useMemo(() => {
    const s = new Set(merged.map((c) => c.profession).filter(Boolean));
    return [...s].sort((a, b) => a.localeCompare(b, "tr"));
  }, [merged]);

  const hometowns = useMemo(
    () =>
      [...new Set(ilceKoyler.map((x) => x.ilce))].sort((a, b) =>
        a.localeCompare(b, "tr")
      ),
    []
  );

  const koyler = useMemo(() => {
    if (!hometown) return [];
    const list = ilceKoyler
      .filter((x) => x.ilce === hometown)
      .map((x) => x.koy);
    const merkez = list.filter((k) =>
      k.toLocaleLowerCase("tr").includes("merkez")
    );
    const diger = list
      .filter((k) => !k.toLocaleLowerCase("tr").includes("merkez"))
      .sort((a, b) => a.localeCompare(b, "tr"));
    return [...merkez, ...diger];
  }, [hometown]);

  /* Debounce */
  const [q, setQ] = useState("");
  useEffect(() => {
    const t = setTimeout(
      () => setQ(search.trim().toLocaleLowerCase("tr")),
      250
    );
    return () => clearTimeout(t);
  }, [search]);

  /* Filtre + sıralama */
  const filtered = useMemo(() => {
    let arr = merged;

    if (q) {
      arr = arr.filter((c) =>
        `${c.name} ${c.profession} ${c.hometown} ${(c.tags || []).join(" ")} ${
          c.bio || ""
        }`
          .toLocaleLowerCase("tr")
          .includes(q)
      );
    }
    if (profession) arr = arr.filter((c) => c.profession === profession);
    if (hometown) arr = arr.filter((c) => c.hometown === hometown);

    if (statusFilter === "active") arr = arr.filter((c) => c.isActive);
    if (statusFilter === "passive") arr = arr.filter((c) => c.isPassive);
    if (statusFilter === "deceased") arr = arr.filter((c) => c.isDeceased);

    if (typeFilter === "celeb")
      arr = arr.filter(
        (c) =>
          c.type !== "politician" &&
          !(c.profession || "").toLocaleLowerCase("tr").includes("siyaset")
      );
    if (typeFilter === "politician")
      arr = arr.filter(
        (c) =>
          c.type === "politician" ||
          (c.profession || "").toLocaleLowerCase("tr").includes("siyaset")
      );
    if (partyFilter)
      arr = arr.filter(
        (c) =>
          (c.party || "").trim().toLocaleUpperCase("tr") ===
          partyFilter.trim().toLocaleUpperCase("tr")
      );

    if (genderFilter) arr = arr.filter((c) => c.gender === genderFilter);

    if (koyFilter) arr = arr.filter((c) => c.koy === koyFilter);

    if (sortBy === "name-asc")
      return [...arr].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", "tr")
      );
    if (sortBy === "name-desc")
      return [...arr].sort((a, b) =>
        (b.name || "").localeCompare(a.name || "", "tr")
      );
    if (sortBy === "year-desc")
      return [...arr].sort((a, b) => (b.born || 0) - (a.born || 0));
    if (sortBy === "year-asc")
      return [...arr].sort((a, b) => (a.born || 0) - (b.born || 0));
    if (sortBy === "newest")
      return [...arr].sort(
        (a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)
      );

    return arr;
  }, [
    merged,
    q,
    profession,
    hometown,
    sortBy,
    statusFilter,
    typeFilter,
    partyFilter,
    genderFilter,
    koyFilter,
  ]);

  /* Sayfalama */
  const [page, setPage] = useState(1);
  const pageSize = 24;
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  useEffect(
    () => setPage(1),
    [
      q,
      profession,
      hometown,
      sortBy,
      statusFilter,
      typeFilter,
      genderFilter,
      koyFilter,
    ]
  );
  const paged = useMemo(
    () => filtered.slice((page - 1) * pageSize, page * pageSize),
    [filtered, page]
  );

  /* Chip'ler */
  const activeChips = [
    q && { k: "Arama", v: search, onClear: () => setSearch("") },
    profession && {
      k: "Meslek",
      v: profession,
      onClear: () => setProfession(""),
    },
    hometown && { k: "İlçe", v: hometown, onClear: () => setHometown("") },
    statusFilter !== "all" && {
      k: "Durum",
      v:
        statusFilter === "active"
          ? "Görevde"
          : statusFilter === "passive"
          ? "Görevde Değil"
          : "Vefat",
      onClear: () => setStatusFilter("all"),
    },
    typeFilter !== "all" && {
      k: "Tür",
      v: typeFilter === "politician" ? "Siyasiler" : "Ünlüler",
      onClear: () => setTypeFilter("all"),
    },
    partyFilter && {
      k: "Parti",
      v: partyFilter,
      onClear: () => setPartyFilter(""),
    },
    genderFilter && {
      k: "Cinsiyet",
      v: genderFilter === "Erkek" ? "Erkek" : "Kadın",
      onClear: () => setGenderFilter(""),
    },
    koyFilter && { k: "Köy", v: koyFilter, onClear: () => setKoyFilter("") },
  ].filter(Boolean);

  const clearAll = () => {
    setSearch("");
    setProfession("");
    setHometown("");
    setSortBy("name-asc");
    setStatusFilter("all");
    setTypeFilter("all");
    setPartyFilter("");
    setGenderFilter("");
    setKoyFilter("");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="container mx-auto px-4 py-6 max-w-screen-xl">
        {/* Başlık */}
        <div className="mb-6">
          <h1 className="text-xl font-semibold text-slate-900 tracking-tight">
            Sivaslı Ünlüler
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            <span className="font-medium text-slate-700">
              {filtered.length}
            </span>{" "}
            kişi listeleniyor
            {fbCelebs.length > 0 && (
              <span className="ml-2 text-emerald-600 font-medium">
                · {fbCelebs.length} yeni ekleme
              </span>
            )}
          </p>
        </div>

        {/* Hata */}
        {error && (
          <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Arama + Filtre barı */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm mb-5">
          {/* Arama + Sıralama */}
          <div className="p-3 flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="İsim, meslek, ilçe veya etiket ara…"
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg
        focus:outline-none focus:ring-2 focus:ring-slate-900 bg-slate-50 placeholder:text-slate-400"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <FilterSelect
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="name-asc">A → Z</option>
              <option value="name-desc">Z → A</option>
              <option value="year-desc">En Yeni Doğum</option>
              <option value="year-asc">En Eski Doğum</option>
              <option value="newest">Son Eklenen</option>
            </FilterSelect>
          </div>

          {/* Filtreler — her zaman görünür */}
          <div className="border-t border-slate-100 px-3 pb-3 pt-2 space-y-2">
            {/* Tür toggle */}
            <div className="flex gap-2">
              {[
                { v: "all", label: "Tümü" },
                { v: "celeb", label: "Ünlüler" },
                { v: "politician", label: "Siyasiler" },
              ].map(({ v, label }) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => {
                    setTypeFilter(v);
                    setStatusFilter("all");
                    setProfession("");
                    setPartyFilter("");
                  }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${
                    typeFilter === v
                      ? "bg-slate-900 text-white border-slate-900"
                      : "border-slate-200 text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Dropdown filtreler */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
              {typeFilter !== "politician" ? (
                <FilterSelect
                  value={profession}
                  onChange={(e) => setProfession(e.target.value)}
                >
                  <option value="">Meslek (Tümü)</option>
                  {professions.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </FilterSelect>
              ) : (
                <FilterSelect
                  value={partyFilter}
                  onChange={(e) => setPartyFilter(e.target.value)}
                >
                  <option value="">Parti (Tümü)</option>
                  {parties.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </FilterSelect>
              )}

              <FilterSelect
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">Durum</option>
                <option value="active">Görevde</option>
                <option value="passive">Görevde Değil</option>
                <option value="deceased">Vefat</option>
              </FilterSelect>

              <FilterSelect
                value={hometown}
                onChange={(e) => {
                  setHometown(e.target.value);
                  setKoyFilter("");
                }}
              >
                <option value="">İlçe (Tümü)</option>
                {hometowns.map((h) => (
                  <option key={h} value={h}>
                    {h}
                  </option>
                ))}
              </FilterSelect>

              <FilterSelect
                value={koyFilter}
                onChange={(e) => setKoyFilter(e.target.value)}
                disabled={!hometown}
              >
                <option value="">
                  {hometown ? "Köy (Tümü)" : "Önce ilçe seçin"}
                </option>
                {koyler.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </FilterSelect>

              <FilterSelect
                value={genderFilter}
                onChange={(e) => setGenderFilter(e.target.value)}
              >
                <option value="">Cinsiyet (Tümü)</option>
                <option value="Erkek">Erkek</option>
                <option value="Kadın">Kadın</option>
              </FilterSelect>

              {activeChips.length > 0 && (
                <button
                  onClick={clearAll}
                  className="flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-xs text-slate-500 border border-slate-200 hover:bg-slate-50 hover:text-slate-800 transition"
                >
                  <X className="w-3 h-3" /> Sıfırla
                </button>
              )}
            </div>
          </div>

          {/* Aktif chip'ler */}
          {activeChips.length > 0 && (
            <div className="flex flex-wrap gap-1.5 px-3 pb-3">
              {activeChips.map((c, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs bg-slate-100 text-slate-700 border border-slate-200"
                >
                  <span className="font-medium">{c.k}:</span> {c.v}
                  <button
                    onClick={c.onClear}
                    className="ml-0.5 rounded-full p-0.5 hover:bg-slate-200 transition"
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Skeleton */}
        {loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl border border-slate-200 p-4 flex gap-3 animate-pulse"
              >
                <div className="w-16 h-16 rounded-xl bg-slate-100 shrink-0" />
                <div className="flex-1 space-y-2 pt-1">
                  <div className="h-3.5 bg-slate-100 rounded w-3/4" />
                  <div className="h-3 bg-slate-100 rounded w-1/2" />
                  <div className="h-3 bg-slate-100 rounded w-2/5" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Kartlar */}
        {!loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3">
            {paged.map((c) => (
              <article
                key={c.id}
                onClick={() => setSelectedPerson(c)}
                className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex gap-3 hover:border-slate-300 hover:shadow-md transition-all duration-150 cursor-pointer"
              >
                <Avatar name={c.name} imageUrl={c.imageUrl} />

                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-2 justify-between">
                    <h3 className="font-semibold text-slate-900 text-sm leading-tight line-clamp-2">
                      {c.name}
                    </h3>
                    <div className="flex flex-wrap items-center gap-1 shrink-0">
                      {c.born && (
                        <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">
                          {c.born}
                        </span>
                      )}
                      <SourceBadge source={c.source} />
                      {c.isDeceased && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200 font-medium">
                          Vefat
                        </span>
                      )}
                      {c.isActive && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium">
                          Görevde
                        </span>
                      )}
                      {c.isPassive && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-medium">
                          Görevde Değil
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="text-xs text-amber-700 font-medium mt-0.5 truncate">
                    {c.profession || "—"}
                  </div>

                  {c.party && (
                    <div className="mt-1">
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200 font-medium">
                        {c.party}
                      </span>
                    </div>
                  )}

                  <div className="text-xs text-slate-500 mt-0.5 truncate">
                    {c.hometown || "Sivas"}
                  </div>

                  {c.bio && (
                    <p className="text-xs text-slate-400 mt-1 line-clamp-2 leading-relaxed">
                      {c.bio}
                    </p>
                  )}

                  {c.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {c.tags.slice(0, 4).map((t) => (
                        <span
                          key={t}
                          className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-100"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </article>
            ))}

            {paged.length === 0 && (
              <div className="col-span-full flex flex-col items-center justify-center py-16 text-slate-400">
                <p className="text-sm">Sonuç bulunamadı.</p>
                <button
                  onClick={clearAll}
                  className="mt-3 text-xs text-slate-500 underline underline-offset-2 hover:text-slate-800"
                >
                  Filtreleri temizle
                </button>
              </div>
            )}
          </div>
        )}

        {/* Sayfalama */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-center gap-1.5">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-2 text-sm rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50 transition"
            >
              ← Önceki
            </button>
            {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
              let n;
              if (totalPages <= 7) n = i + 1;
              else if (page <= 4) n = i + 1;
              else if (page >= totalPages - 3) n = totalPages - 6 + i;
              else n = page - 3 + i;
              return (
                <button
                  key={n}
                  onClick={() => setPage(n)}
                  className={`w-9 h-9 text-sm rounded-lg border transition ${
                    page === n
                      ? "bg-slate-900 text-white border-slate-900 font-medium"
                      : "border-slate-200 hover:bg-slate-50 text-slate-600"
                  }`}
                >
                  {n}
                </button>
              );
            })}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-2 text-sm rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50 transition"
            >
              Sonraki →
            </button>
          </div>
        )}
      </div>
      {selectedPerson && (
        <CelebDetailModal
          person={selectedPerson}
          onClose={() => setSelectedPerson(null)}
        />
      )}
    </div>
  );
}
